import { useEffect, useState } from "react";
import { Plus, Tag, Timer } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";
import { imgProps } from "../lib/img";
import { EditEntityButton } from "./edit/EntityEditor";

export function FlashCountdown({ endsAt }) {
  const [left, setLeft] = useState(() => (endsAt ? new Date(endsAt) - Date.now() : 0));
  useEffect(() => {
    if (!endsAt) return undefined;
    const t = setInterval(() => setLeft(new Date(endsAt) - Date.now()), 1000);
    return () => clearInterval(t);
  }, [endsAt]);
  if (!endsAt || left <= 0) return null;
  const h = Math.floor(left / 3600000), m = Math.floor((left % 3600000) / 60000), s = Math.floor((left % 60000) / 1000);
  const txt = h >= 24 ? `${Math.floor(h / 24)}d ${h % 24}h` : `${h}h ${String(m).padStart(2, "0")}m ${String(s).padStart(2, "0")}s`;
  return (
    <span className="flex items-center gap-1 bg-[#0F0F0F]/90 text-white text-[10px] font-bold px-2 py-0.5 rounded-full" data-testid="flash-countdown">
      <Timer size={10} aria-hidden="true" /> Ends in {txt}
    </span>
  );
}

export function SalePrice({ product, dark }) {
  if (product.coming_soon) {
    return <span className="text-xs font-bold uppercase tracking-wide text-amber-600" data-testid={`price-${product.id}`}>Coming soon</span>;
  }
  if (!product.display_price) {
    return <span className="text-sm font-semibold text-neutral-400" data-testid={`price-${product.id}`}>Price TBD</span>;
  }
  return (
    <div className="flex items-baseline gap-2">
      <span className={`font-bold ${dark ? "text-black" : "text-[#0F0F0F]"}`} data-testid={`price-${product.id}`}>
        ${product.display_price.toFixed(2)}
      </span>
      {product.on_sale && (
        <s className="text-sm text-neutral-400" data-testid={`regular-price-${product.id}`}>
          ${product.regular_price.toFixed(2)}
        </s>
      )}
    </div>
  );
}

function salePct(product) {
  if (!product.on_sale || !product.regular_price || !product.display_price) return null;
  const pct = Math.round((1 - product.display_price / product.regular_price) * 100);
  return pct > 0 ? pct : null;
}

export function DietaryTags({ tags, className = "" }) {
  if (!tags?.length) return null;
  return (
    <div className={`flex flex-wrap gap-1 ${className}`} data-testid="dietary-tags">
      {tags.map((t) => <span key={t} className="text-[10px] font-bold uppercase tracking-wide bg-green-50 text-green-800 border border-green-200 px-1.5 py-0.5 rounded-full">{t}</span>)}
    </div>
  );
}

export default function ProductCard({ product, deal, onOpen, onEdited, categories }) {
  const { addFood, setCoupon, setDrawerOpen } = useCart();
  const pct = salePct(product);

  const applyDeal = async (e) => {
    e.stopPropagation();
    if (!product.in_stock || !product.display_price) { toast.error(`${product.name} is sold out today`); return; }
    const qty = deal.type === "bogo_free" || deal.type === "bogo_half" ? 2
      : deal.type === "buy_x_get_y" ? (deal.buy_qty || 1) + (deal.get_qty || 1) : 1;
    if (!(await addFood(product, qty))) return;
    if (deal.code) setCoupon(deal.code);
    if (deal.type === "bogo_free") toast.success(`Deal applied — 2 added, the 2nd one is FREE at checkout!`);
    else if (deal.type === "bogo_half") toast.success(`Deal applied — 2 added, the 2nd one is 50% off at checkout!`);
    else if (deal.type === "buy_x_get_y") toast.success(`Deal applied — ${qty} added, discount applies at checkout!`);
    else if (deal.type === "bundle") toast.success(`${product.name} added — add the other bundle items to unlock the set price`);
    else if (deal.code) toast.success(`${deal.code} applied — ${deal.label.toLowerCase()} at checkout!`);
    else toast.success(`${product.name} added — ${deal.name} discount applies automatically at checkout`);
    setDrawerOpen(true);
  };

  const soldOut = product.in_stock === false;
  return (
    <div className={`bg-white rounded-2xl overflow-hidden hover-lift border border-black/5 ${onOpen ? "cursor-pointer" : ""}`}
      onClick={() => onOpen?.(product)} role={onOpen ? "button" : undefined}
      data-testid={`product-card-${product.id}`} data-sold-out={soldOut ? "true" : undefined}>
      <div className="relative h-40 sm:h-44 overflow-hidden">
        {product.image ? (
          <img {...imgProps(product.image, [320, 480, 640], "(min-width: 1024px) 25vw, (min-width: 640px) 50vw, 100vw")} alt={product.name} width="480" height="176" className={`w-full h-full object-cover ${soldOut ? "grayscale" : ""}`} loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center p-4" style={{ background: "#F3EBDD" }} data-testid={`product-tile-${product.id}`}>
            <span className="text-xs font-semibold tracking-wide text-center" style={{ color: "#B8860B" }}>{product.name}</span>
          </div>
        )}
        {!soldOut && (
        <div className="absolute top-3 left-3 flex flex-col items-start gap-1.5">
          {product.bogo && (
            <span className="flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full shadow-md uppercase tracking-wide" style={{ background: "#B8860B", color: "#fff" }} data-testid={`bogo-badge-${product.id}`}>
              Buy 1 Get 1 Free
            </span>
          )}
          {product.site_badge && (
            <span className="flex items-center gap-1 bg-[#9E3B3B] text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-md uppercase tracking-wide" data-testid={`site-badge-${product.id}`}>
              {product.site_badge}
            </span>
          )}
          {product.special?.group === "weekend" && (
            <span className="flex items-center gap-1 bg-[#1a1410] text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-md" data-testid={`weekend-chip-${product.id}`}>
              WEEKEND ONLY
            </span>
          )}
          {product.todays_special && (
            <span className="flex items-center gap-1 bg-[#F5B841] text-black text-[10px] font-bold px-2 py-0.5 rounded-full shadow-md" data-testid={`special-chip-${product.id}`}>
              ★ TODAY'S SPECIAL
            </span>
          )}
          {deal && (
            <button onClick={applyDeal} data-testid={`deal-chip-${product.id}`}
              className="flex items-center gap-1 bg-[#E04F33] hover:bg-[#C94228] text-white text-[11px] font-bold px-2.5 py-1 rounded-full shadow-md transition-colors cursor-pointer">
              <Tag size={11} aria-hidden="true" /> {deal.label}
            </button>
          )}
          {deal?.is_flash && deal.expires_at && <FlashCountdown endsAt={deal.expires_at} />}
          {product.on_sale && (
            <span className="bg-[#E04F33] text-white text-[11px] font-bold px-2.5 py-1 rounded-full" data-testid={`sale-badge-${product.id}`}>
              {pct ? `${pct}% OFF` : "SALE"}
            </span>
          )}
        </div>
        )}
        <EditEntityButton type="product" entity={product} ctx={{ categories }} className="absolute top-3 right-3" onSaved={onEdited} />
        {soldOut && (
          <div className="absolute inset-0 bg-white/75 flex items-center justify-center" data-testid={`sold-out-${product.id}`}>
            <span className="text-sm font-extrabold uppercase tracking-wide bg-neutral-800 text-white px-3 py-1.5 rounded-full">Sold out today</span>
          </div>
        )}
      </div>
      <div className="p-4">
        <h3 className="font-display font-semibold text-[15px] leading-tight">
          {product.slug ? <a href={`https://tryurbanturban.com/menu/${product.slug}`} onClick={(e) => e.preventDefault()} data-testid={`dish-link-${product.id}`}>{product.name}</a> : product.name}
        </h3>
        <p className="text-xs text-neutral-500 mt-1 line-clamp-2">{product.description}</p>
        <DietaryTags tags={product.dietary_tags} className="mt-1.5" />
        <div className="mt-3 flex items-center justify-between">
          <SalePrice product={product} />
          {!product.coming_soon && (
          <button
            data-testid={`add-to-cart-${product.id}`}
            disabled={soldOut || !product.display_price}
            aria-label={soldOut ? `${product.name} is sold out today` : `Add ${product.name}`}
            onClick={async (e) => {
              e.stopPropagation();
              if (onOpen) onOpen(product);
              else if (await addFood(product)) toast.success(`${product.name} added to cart`);
            }}
            className="w-9 h-9 rounded-full bg-[#E04F33] hover:bg-[#C94228] disabled:bg-neutral-300 text-white flex items-center justify-center transition-colors">
            <Plus size={17} aria-hidden="true" />
          </button>
          )}
        </div>
      </div>
    </div>
  );
}
